// src/components/QRScannerClass.js
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { firestore, auth } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { StudentContext } from '../StudentContext';
import { subscribeCatechistSubstitute } from '../FirebaseService';
import qrScanIcon from './access/qr-code-scan.svg';
import './QRScannerClass.css';

const QRScannerClass = () => {
  const { currentYearId, studentsData, classMap, updateAttendanceInContext } = useContext(StudentContext);

  const [scanResults, setScanResults] = useState(() => {
    const savedResults = sessionStorage.getItem('scanResultsCatechism');
    return savedResults ? JSON.parse(savedResults).map(result => ({
      ...result,
      timestamp: new Date(result.timestamp)
    })) : [];
  });

  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // 🌟 State lưu trữ thông tin phân công phụ lớp của GLV hiện tại
  const [substituteInfo, setSubstituteInfo] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState('');

  const catechistFullName = localStorage.getItem('catechistFullName') || 'Giáo lý viên';
  const catechistHolyName = localStorage.getItem('catechistHolyName') || '';
  const catechistClassName = localStorage.getItem('catechistClassName') || '';
  const currentUid = localStorage.getItem('uid') || auth.currentUser?.uid;
  const scannerRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('scanResultsCatechism', JSON.stringify(scanResults));
  }, [scanResults]);

  // ID lớp chính
  const primaryClassId = useMemo(() => {
    return Object.keys(classMap).find(key => classMap[key] === catechistClassName) || catechistClassName;
  }, [classMap, catechistClassName]);

  // 🌟 LẮNG NGHE PHÂN CÔNG PHỤ LỚP THỜI GIAN THỰC CỦA GLV HIỆN TẠI
  useEffect(() => {
    if (!currentYearId || !currentUid) return;
    const unsubscribe = subscribeCatechistSubstitute(currentYearId, currentUid, (data) => {
      setSubstituteInfo(data);
    });
    return () => unsubscribe && unsubscribe();
  }, [currentYearId, currentUid]);

  // 🌟 TỔNG HỢP DANH SÁCH LỚP KHẢ DỤNG (LỚP CHÍNH + CÁC LỚP PHỤ)
  const availableClasses = useMemo(() => {
    const list = [];
    if (primaryClassId) {
      list.push({
        id: primaryClassId,
        name: classMap[primaryClassId] || catechistClassName || primaryClassId,
        isPrimary: true
      });
    }

    if (substituteInfo?.assignedClasses && Array.isArray(substituteInfo.assignedClasses)) {
      substituteInfo.assignedClasses.forEach(item => {
        const cId = typeof item === 'string' ? item : item.classId;
        const cName = (typeof item === 'object' && item.className) ? item.className : (classMap[cId] || cId);
        if (cId && !list.some(x => x.id === cId)) {
          list.push({
            id: cId,
            name: cName,
            isPrimary: false
          });
        }
      });
    }
    return list;
  }, [primaryClassId, catechistClassName, classMap, substituteInfo]);

  // Lớp đang được chọn để quét
  const activeClassId = useMemo(() => {
    if (selectedClassId && availableClasses.some(c => c.id === selectedClassId)) {
      return selectedClassId;
    }
    return availableClasses[0]?.id || primaryClassId || '';
  }, [selectedClassId, availableClasses, primaryClassId]);

  const activeClassObj = useMemo(() => {
    return availableClasses.find(c => c.id === activeClassId) || null;
  }, [availableClasses, activeClassId]);

  // Danh sách thiếu nhi của lớp đang quét
  const students = useMemo(() => {
    return studentsData[activeClassId] || [];
  }, [studentsData, activeClassId]);

  useEffect(() => {
    if (manualInput.trim() === '') {
      setSuggestions([]);
      return;
    }
    const filtered = students.filter(s =>
      s.id.toLowerCase().includes(manualInput.toLowerCase().trim()) ||
      s.fullName.toLowerCase().includes(manualInput.toLowerCase().trim())
    );
    setSuggestions(filtered);
  }, [manualInput, students]);

  useEffect(() => {
    if (scanning && !scannerRef.current) {
      setTimeout(() => {
        try {
          scannerRef.current = new Html5QrcodeScanner(
            'reader-class',
            {
              fps: 10,
              qrbox: { width: 220, height: 220 },
              facingMode: 'environment',
              showTorchButton: true,
              showZoomSlider: true,
            },
            false
          );
          scannerRef.current.render(onScanSuccess, () => { });
        } catch (error) {
          console.error('Lỗi scanner:', error);
          setScanning(false);
        }
      }, 100);
    } else if (!scanning && scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, [scanning]);

  const addScanResult = (result) => {
    setScanResults((prev) => {
      if (result.status !== 'processing') {
        const newResults = prev.map((item) =>
          item.studentId === result.studentId && item.status === 'processing' ? result : item
        );
        if (!newResults.some((item) => item.studentId === result.studentId)) {
          return [result, ...newResults].slice(0, 15);
        }
        return newResults.slice(0, 15);
      } else {
        return [result, ...prev].slice(0, 15);
      }
    });
  };

  const onScanSuccess = async (decodedText) => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    setScanning(false);
    await processAttendance(decodedText.trim());
  };

  const processAttendance = async (studentId) => {
    const now = new Date();
    const dateKey = format(now, 'yyyy-MM-dd');
    const timeString = format(now, 'HH:mm');

    addScanResult({ studentId, name: 'Đang xử lý...', timestamp: now, status: 'processing' });

    try {
      const studentSnap = await getDoc(doc(firestore, 'students', studentId));
      if (!studentSnap.exists()) {
        addScanResult({ studentId, name: 'Mã ID không tồn tại', timestamp: now, status: 'error' });
        return;
      }

      const studentData = studentSnap.data();
      const studentName = `${studentData.holyName || ''} ${studentData.fullName || ''}`.trim();

      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, studentId);

      await setDoc(docRef, {
        catechism: {
          [dateKey]: {
            time: `Có lúc ${timeString}`,
            catechist: catechistFullName,
            time_log: now.toISOString(),
          }
        }
      }, { merge: true });

      if (typeof updateAttendanceInContext === 'function') {
        updateAttendanceInContext(studentId, 'catechism', dateKey, `Có lúc ${timeString}`, catechistFullName);
      }

      addScanResult({ studentId, name: studentName, timestamp: now, status: 'success' });

    } catch (error) {
      console.error('Lỗi khi điểm danh học giáo lý:', error);
      addScanResult({ studentId, name: 'Lỗi ghi dữ liệu', timestamp: now, status: 'error' });
    }
  };

  const handleSuggestionClick = (student) => {
    setSelectedStudent(student);
    setManualInput('');
    setSuggestions([]);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmManualAttendance = async () => {
    if (selectedStudent) {
      await processAttendance(selectedStudent.id);
      setIsConfirmModalOpen(false);
      setSelectedStudent(null);
    }
  };

  return (
    <div className="scan-container">
      {/* Badge Thông tin GLV Rút gọn */}
      <div className="glv-compact-badge catechism-badge">
        <span className="badge-role">📖 ĐIỂM DANH HỌC GIÁO LÝ</span>
        <span className="badge-name">
          GLV: {catechistHolyName} <strong>{catechistFullName}</strong>
        </span>
      </div>

      {/* 🌟 THANH CHỌN CHUYỂN ĐỔI LỚP QUÉT (KHI CÓ ĐƯỢC PHÂN CÔNG PHỤ LỚP) 🌟 */}
      {availableClasses.length > 1 && (
        <div className="glv-class-switcher-bar">
          <label className="switcher-label">🏫 Điểm danh cho lớp:</label>
          <select
            value={activeClassId}
            onChange={(e) => setSelectedClassId(e.target.value)}
            className="switcher-select"
          >
            {availableClasses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.isPrimary ? `⭐ ${c.name} (Lớp chính)` : `🤝 ${c.name} (Lớp phụ)`}
              </option>
            ))}
          </select>

          {activeClassObj && !activeClassObj.isPrimary && (
            <span className="substitute-notice-badge">
              🤝 Đang phụ lớp
            </span>
          )}
        </div>
      )}

      {/* Tìm kiếm & Điểm danh thủ công */}
      <div className="manual-input-container">
        <input
          type="text"
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder={`🔍 Tìm Mã ID hoặc Tên trong lớp ${activeClassObj?.name || ''}...`}
          className="manual-input"
        />
        {suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map(student => (
              <li
                key={student.id}
                onClick={() => handleSuggestionClick(student)}
                className="suggestion-item"
              >
                <strong>{student.id}</strong> - {student.holyName || ''} {student.fullName}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bảng Nhật Ký Quét */}
      <div className="scan-results-table">
        <table>
          <thead>
            <tr>
              <th>Thời gian</th>
              <th>Mã ID</th>
              <th>Họ và Tên</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {scanResults.length > 0 ? (
              scanResults.map((result, index) => (
                <tr key={index} className={result.status}>
                  <td>{format(result.timestamp, 'HH:mm:ss')}</td>
                  <td className="td-id">{result.studentId}</td>
                  <td className="td-name">{result.name}</td>
                  <td className="td-status">
                    {result.status === 'processing' && <div className="spinner"></div>}
                    {result.status === 'success' && <span className="icon-success">✓</span>}
                    {result.status === 'error' && <span className="icon-error">✗</span>}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="empty-scan-msg">
                  Chưa có nhật ký điểm danh trong phiên này.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* NÚT FAB NỔI BÊN PHẢI */}
      <button className="fab-scan-btn catechism-theme" onClick={() => setScanning(true)}>
        <img src={qrScanIcon} alt="Scan QR" className="fab-qr-icon" />
        <span className="fab-text">Quét mã QR</span>
      </button>

      {/* MODAL POPUP QUÉT MÃ */}
      {scanning && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-card">
            <div className="qr-modal-header">
              <div className="header-title-box">
                <img src={qrScanIcon} alt="Scan" className="modal-header-icon" />
                <h4>Bỏ Mã QR Vào Khung Quét</h4>
              </div>
              <button className="qr-close-btn" onClick={() => setScanning(false)}>✕</button>
            </div>
            <div className="qr-modal-body">
              <div id="reader-class"></div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL XÁC NHẬN THỦ CÔNG */}
      {isConfirmModalOpen && selectedStudent && (
        <div className="qr-modal-overlay">
          <div className="confirm-card">
            <h3>Xác Nhận Điểm Danh Thủ Công</h3>
            <div className="student-info-box">
              <p><strong>Mã ID:</strong> <span className="highlight">{selectedStudent.id}</span></p>
              <p><strong>Tên Thánh:</strong> {selectedStudent.holyName || 'Chưa có'}</p>
              <p><strong>Họ và Tên:</strong> {selectedStudent.fullName}</p>
            </div>
            <div className="confirm-actions">
              <button className="btn-confirm" onClick={handleConfirmManualAttendance}>Đồng ý</button>
              <button className="btn-cancel" onClick={() => setIsConfirmModalOpen(false)}>Hủy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScannerClass;